import { useAuth0 } from '@auth0/auth0-react';
import {
  BaseOpt,
  CallbackStatus,
  CartDetails,
  CompletionApplyData,
  CompletionCheckoutData,
  ConsumerDetails,
  EnvironmentType,
  MerchantApplyOpt,
  MerchantCheckoutOpt,
  MerchantIdentificationCollectionOpt,
  OnDataUpdateCallBackData,
  initialize,
} from '@chargeafter/payment-sdk';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithReset, useResetAtom } from 'jotai/utils';
import { useEffect } from 'react';
import { useIdleTimerContext } from 'react-idle-timer';
import { useFlipScreen } from '../FlipScreenProvider';
import { AppView, FlipOrigin, SELF_FINANCING_PRODUCT } from '../constants';
import useFeedbackSnackbar from '../hooks/useFeedbackSnackbar';
import useGetPromotionCode from '../modals/usePromotionsModal';
import useSettlementConfigurationModal from '../modals/useSettlementConfigurationModal';
import { applyResultSchema, validateData } from '../validations';
import { awaiter } from './awaiter';
import {
  useGetAdvancedCartDetails,
  useGetCart,
  useGetCartTypeToggle,
  useGetMerchantOrderId,
  useResetAdvancedCartDetails,
  useSetManualMerchantOrderId,
  useSetSingleItemCart,
} from './cartManager';
import {
  SettleOptions,
  useGetMerchantConfigurationOptions,
  useGetMerchantCurrency,
  useGetMerchantEnvPublicKey,
  useGetSelectedSettleOption,
} from './configManager';
import {
  addToContext,
  clearContext,
  clearUserFromContext,
  removeFromContext,
} from './contextManager';
import {
  useConsumerData,
  useGetConsumerId,
  useGetSignedApplicationId,
  useResetConsumerId,
  useSetSelectedConsumerStatus,
} from './customersTabManager';
import { FEEDBACK_TYPE } from './feedbackManager';
import {
  getFsSessionUrl,
  startFsRecording,
  stopFsRecording,
} from './fullStoryManager';
import {
  getDisplayedReferenceId,
  useGetApplyConfiguration,
  useGetCheckoutConfiguration,
  useGetIdVerificationRequired,
} from './lendersConfigurationManager';
import logger from './loggerManager';
import { useSetCurrentModal } from './modalManager';
import { setFirstLoadAfterLogin, useOpenPinScreen } from './pinManager';
import { tracker } from './trackerManager';
import { useGetUserPermissions, useGetUserProfile } from './userManager';
import {
  clearSavedUserSession,
  restoreUserSession,
  saveToUserSession,
  userSession,
} from './userSessionStorageManager';
import { getStoreFromStorage, useGetStoreId } from './storeManager';
import { BaseLender } from '@chargeafter/payment-sdk/payment-types/src/types/lender';
import { environment } from '../environment';

restoreUserSession();

export type CartItem = CartDetails['items'][0];
export type CartDiscounts = CartDetails['discounts'];

export type nullableAmountAvailableCredit = {
  lender: BaseLender;
  creditAmount?: number;
  minimumAmount?: number;
  financialProductType: string;
};

const ApprovedAmountAtom = atom(userSession?.creditAmount ?? 0);

const IsApprovedAmountEstimated = atom(
  userSession?.isApprovedAmountEstimated ?? false,
);

const CheckoutMinimumAmount = atom(userSession?.checkoutMinimumAmount ?? null);

const FinancialProductType = atom(userSession?.financialProductType ?? null);

const ApprovingLenderState = atom(userSession?.lenderId ?? '');

const PrequalifyConfirmationTokenState = atom(userSession?.applicationId);

const ConsumerDetailsState = atom<ConsumerDetails | undefined>(
  userSession?.consumerDetails,
);

const SettlementData = atomWithReset({
  shouldSettle: false,
  receivedData: false,
  orderId: '',
  lenderReferenceId: '',
  approvalCode: '',
});

const SdkLoading = atom(false);

const SdkDisplayed = atom(false);

const OfferDescription = atom('');

export type OptionalTabs = 'cart' | 'customers' | 'pending' | 'transactions';
const SelectedTab = atom<OptionalTabs>('cart');

const PageNameState = atom<AppView | undefined>(undefined);

export const useGetApprovedAmount = () => useAtomValue(ApprovedAmountAtom);
export const useSetApprovedAmount = () => useSetAtom(ApprovedAmountAtom);

export const useGetIsApprovedAmountEstimated = () =>
  useAtomValue(IsApprovedAmountEstimated);
export const useSetIsApprovedAmountEstimated = () =>
  useSetAtom(IsApprovedAmountEstimated);

export const useGetCheckoutMinimumAmount = () =>
  useAtomValue(CheckoutMinimumAmount);
export const useSetCheckoutMinimumAmount = () =>
  useSetAtom(CheckoutMinimumAmount);

export const useGetFinancialProductType = () =>
  useAtomValue(FinancialProductType);
export const useSetFinancialProductType = () =>
  useSetAtom(FinancialProductType);

export const useGetApprovingLender = () => useAtomValue(ApprovingLenderState);
export const useSetApprovingLender = () => useSetAtom(ApprovingLenderState);

export const useGetPrequalifyConfirmationToken = () =>
  useAtomValue(PrequalifyConfirmationTokenState);
export const useSetPrequalifyConfirmationToken = () =>
  useSetAtom(PrequalifyConfirmationTokenState);

export const useGetConsumer = () => useAtomValue(ConsumerDetailsState);
export const useSetConsumer = () => useSetAtom(ConsumerDetailsState);

export const useGetSettlementData = () => useAtomValue(SettlementData);
export const useSetSettlementData = () => useSetAtom(SettlementData);

export const useGetSdkLoading = () => useAtomValue(SdkLoading);
export const useSetSdkLoading = () => useSetAtom(SdkLoading);

export const useGetSdkDisplayed = () => useAtomValue(SdkDisplayed);
export const useSetSdkDisplayed = () => {
  const setSdkDisplayed = useSetAtom(SdkDisplayed);
  const { start, pause } = useIdleTimerContext();

  return (displayed: boolean) => {
    setSdkDisplayed(displayed);
    if (displayed) {
      pause();
      return;
    }
    start();
  };
};

export const useGetSelectedTab = () => useAtomValue(SelectedTab);
export const useSetSelectedTab = () => useSetAtom(SelectedTab);

export const useGetOfferDescription = () => useAtomValue(OfferDescription);
export const useSetOfferDescription = () => useSetAtom(OfferDescription);

export const useGetPageName = () => useAtomValue(PageNameState);
export const useSetPageName = () => useSetAtom(PageNameState);

export const useApplyStartOver = () => {
  const { user } = useAuth0();
  const resetAmount = useSetApprovedAmount();
  const resetEstimated = useSetIsApprovedAmountEstimated();
  const resetMinimum = useSetCheckoutMinimumAmount();
  const resetFinancialProduct = useSetFinancialProductType();
  const resetLender = useSetApprovingLender();
  const resetPrequalifyConfirmationToken = useSetPrequalifyConfirmationToken();
  const resetConsumer = useSetConsumer();
  const resetConsumerStatus = useSetSelectedConsumerStatus();
  const resetConsumerId = useResetConsumerId();

  return (stopTracking: boolean = true, selectedConsumer: boolean = false) => {
    if (!selectedConsumer) {
      resetConsumerId();
    }
    if (stopTracking) {
      clearSavedUserSession();
      try {
        stopFsRecording(user?.email ?? '');
      } catch (e) {
        logger.error(`Failed to stop fullstory recording. error: ${e}`, {
          filename: 'flowManager.ts',
          function: 'useApplyStartOver',
        });
      }

      try {
        tracker.reset();
      } catch (e) {
        logger.error(`Failed to reset mixpanel. error: ${e}`, {
          filename: 'flowManager.ts',
          function: 'useApplyStartOver',
        });
      }
    }
    clearUserFromContext();
    resetConsumer(undefined);
    resetConsumerStatus(undefined);
    resetAmount(0);
    resetEstimated(false);
    resetMinimum(null);
    resetFinancialProduct(null);
    resetLender('');
    resetPrequalifyConfirmationToken(undefined);
  };
};

export const useStartOver = () => {
  const applyStartOver = useApplyStartOver();

  const resetCartItems = useSetSingleItemCart();
  const resetAdvancedCart = useResetAdvancedCartDetails();
  const resetMerchantOrderId = useSetManualMerchantOrderId();
  const resetSettlementData = useResetAtom(SettlementData);
  return () => {
    applyStartOver();
    resetCartItems(new Array<CartItem>(0));
    resetAdvancedCart();
    resetMerchantOrderId(undefined);
    resetSettlementData();
  };
};

export const initAppWindowEvents = () => {
  const onPageShow = () => {
    setFirstLoadAfterLogin(false);
    const pageHideEvent = localStorage.getItem('pagehide');
    if (pageHideEvent) {
      logger.log('Page closed', {
        filename: 'flowManager.ts',
        function: 'onPageShow',
        eventData: pageHideEvent,
      });
      localStorage.removeItem('pagehide');
    }
  };

  const onPageHide = () => {
    const pageHidData = {
      date: new Date(),
      fullStoryLink: getFsSessionUrl(false),
    };
    localStorage.setItem('pagehide', JSON.stringify(pageHidData));
  };

  const onError = (e: Event | string) => {
    logger.error('Window error event', {
      function: 'onError',
      filename: 'flowManager.ts',
      error: JSON.stringify(e),
    });
  };

  window.addEventListener('pageshow', onPageShow);
  window.addEventListener('pagehide', onPageHide);
  window.addEventListener('error', onError);
};

export const useInitAppFullStory = () => {
  const { user } = useAuth0();
  const storeId = useGetStoreId();
  useEffect(() => {
    if (user && storeId) {
      startFsRecording(user?.email ?? '', storeId);
    }
  }, [user, user?.email, storeId]);
};

const useInitConfigProps = (): BaseOpt => {
  return {
    channel: 'IN_STORE' as any,
    posType: 'GenericMerchantDevice',
  };
};

const useInitializaPaymentSdk = () => {
  const env = environment.envName as EnvironmentType;
  const envKey = useGetMerchantEnvPublicKey();
  const currency = useGetMerchantCurrency();
  const user = useGetUserProfile();

  return async (storeId: string) => {
    const initializePayload = {
      currency,
      apiKey: envKey,
      storeId,
      userId: user?.userId,
      preferences: {
        currency,
      },
    };
    logger.log('Initializing sdk', {
      filename: 'flowManager',
      function: 'useInitializaPaymentSdk',
      payload: initializePayload,
    });

    const ca = await initialize(initializePayload, env);

    return {
      identify: ca.identification.present,
      payments: ca.payments.present,
      updateSdkConfig: ca.updateConfig,
    };
  };
};

const getIsErrorNotAllowed = (error: string) => {
  const allowedErrors = ['CONSUMER_CANCELLED', 'BACK_TO_STORE', 'PENDING'];
  return !allowedErrors.includes(error);
};

const useIdentify = () => {
  const setIsModalDisplayed = useSetSdkDisplayed();
  const setModalLoading = useSetSdkLoading();
  const [feedback] = useFeedbackSnackbar();
  const currency = useGetMerchantCurrency();
  const lenderRequiringIdentification = useGetIdVerificationRequired('Apply');
  const ca = useInitializaPaymentSdk();
  const storeId = useGetStoreId();

  return async (merchantLogoUrl: string) => {
    if (lenderRequiringIdentification) {
      try {
        setIsModalDisplayed(true);
        setModalLoading(true);

        const identifyPayload: MerchantIdentificationCollectionOpt = {
          country: '',
          language: 'en',
          merchantLogoUrl: merchantLogoUrl,
          currency,
        };

        logger.log('Starting identify', {
          filename: 'flowManager',
          function: 'useIdentify',
          payload: identifyPayload,
        });

        const sdk = await ca(storeId);
        const data = await sdk.identify(identifyPayload);

        logger.log('Identify completed', {
          filename: 'flowManager',
          function: 'useIdentify',
          resultData: data,
        });

        return data;
      } catch (e) {
        const ex = e as CallbackStatus;
        const notAllowedErrors = ex?.code !== 'CONSUMER_CANCELLED';
        logger[notAllowedErrors ? 'error' : 'warn'](
          `Identify failed. error: ${JSON.stringify(ex)}`,
          {
            filename: 'flowManager.ts',
            function: 'identify',
          },
        );
        if (notAllowedErrors) {
          feedback({
            type: FEEDBACK_TYPE.IDENTIFICATION_ERROR,
          });
        }
        throw new Error('Identification required');
      } finally {
        setModalLoading(false);
        setIsModalDisplayed(false);
      }
    }
    return;
  };
};

export const useApply = (
  stopLoading: () => void,
): (() => Promise<ConsumerDetails | undefined>) => {
  const { user } = useAuth0();
  const { start } = useIdleTimerContext();
  const { flipScreen: displayFlipScreen } =
    useGetMerchantConfigurationOptions();
  const setAmount = useSetApprovedAmount();
  const setMinimum = useSetCheckoutMinimumAmount();
  const setFinancialProductType = useSetFinancialProductType();
  const setEstimated = useSetIsApprovedAmountEstimated();
  const setLender = useSetApprovingLender();
  const setConsumer = useSetConsumer();
  const setPrequalifyConfirmation = useSetPrequalifyConfirmationToken();
  const config = useInitConfigProps();
  const flipScreen = useFlipScreen();
  const currency = useGetMerchantCurrency();

  const setSdkDisplayed = useSetSdkDisplayed();
  const setSdkLoading = useSetSdkLoading();

  const [feedback] = useFeedbackSnackbar();

  const openPINScreen = useOpenPinScreen();
  const sdkDisplayed = useGetSdkDisplayed();
  const userPermissions = useGetUserPermissions();
  const updateCurrentModal = useSetCurrentModal();
  const applyConfig = useGetApplyConfiguration();
  const identification = useIdentify();
  const ca = useInitializaPaymentSdk();
  const storeId = useGetStoreId();

  const apply = async () => {
    try {
      if (!userPermissions.ApplyExecute) {
        updateCurrentModal('applyDisabled');
        return;
      }

      if (!applyConfig) {
        feedback({
          type: FEEDBACK_TYPE.GENERAL_ERROR,
        });
        throw new Error('missing configuration');
      }

      const sdk = await ca(storeId);
      await sdk.updateSdkConfig({
        storeId,
      });

      const identificationToken = await identification(
        applyConfig.branding.logo ?? '',
      );
      try {
        stopLoading();
        if (displayFlipScreen) {
          await flipScreen({ toCustomer: true, origin: FlipOrigin.ApplyStart });
        }

        setSdkDisplayed(true);
        setSdkLoading(true);

        const applyOpts: MerchantApplyOpt = {
          ...config,
          identificationToken,
          onModalOpen: () => setSdkLoading(false),
          currency,
        };

        logger.log('Starting prequalify', {
          filename: 'flowManager',
          function: 'useApply',
          applyProps: applyOpts,
        });

        const applyResponse = await sdk.payments('apply', applyOpts);
        if (applyResponse.status) throw applyResponse;

        logger.log('Prequalify completed', {
          filename: 'flowManager',
          function: 'useApply',
          resultData: applyResponse,
        });

        const applyData = applyResponse.data;
        if (!applyData) {
          throw new Error('No apply data returned');
        }

        const applyResultError = await validateData(
          applyData,
          applyResultSchema,
        );
        if (applyResultError) throw applyResultError;
        const credit = applyData.availableCredit[0];
        const lenderId = credit.lender.id;
        const updatedCredit = {
          ...credit,
          creditAmount:
            credit.financialProductType !== SELF_FINANCING_PRODUCT
              ? credit.creditAmount
              : undefined,
        };

        addToContext('applicationId', applyData.applicationId);
        addToContext('consumer', applyData.consumerDetails);
        addToContext('availableCredit', updatedCredit);

        saveToUserSession('userEmail', user?.email);

        setFinancialProductType(updatedCredit.financialProductType ?? null);
        saveToUserSession(
          'financialProductType',
          updatedCredit.financialProductType,
        );

        const lenderDoesNotSupportOtb = applyConfig.lendersConfigurations.find(
          (lender) => lender.id === lenderId,
        )?.configuration.doesNotSupportOtb;

        const updatedAmount =
          updatedCredit.financialProductType !== SELF_FINANCING_PRODUCT &&
          !lenderDoesNotSupportOtb
            ? updatedCredit.creditAmount ?? 0
            : 0;

        setAmount(updatedAmount);
        saveToUserSession('creditAmount', updatedAmount);
        setEstimated(false);
        saveToUserSession('isApprovedAmountEstimated', false);

        setMinimum(updatedCredit.minimumAmount ?? null);
        saveToUserSession('checkoutMinimumAmount', updatedCredit.minimumAmount);

        setLender(lenderId);
        saveToUserSession('lenderId', lenderId);

        const consumerDetails = {
          ...applyData.consumerDetails,
          id: applyData.consumerId,
        };
        setConsumer(consumerDetails);
        saveToUserSession('consumerDetails', consumerDetails);

        const applicationId = applyData.applicationId ?? '';
        if (!applicationId) throw new Error('Failed to get applicationId');

        setPrequalifyConfirmation(applicationId);
        saveToUserSession('applicationId', applicationId);

        tracker.identify(
          (applyData.consumerDetails?.mobilePhoneNumber ??
            applyData.consumerId)!,
        );

        if (displayFlipScreen) {
          start();
          await flipScreen({
            origin: FlipOrigin.ApplySuccess,
            toCustomer: false,
          });
        }

        openPINScreen(sdkDisplayed);

        return applyData.consumerDetails;
      } catch (e: unknown) {
        logger.log('Prequalify threw', {
          filename: 'flowManager',
          function: 'useApply',
        });
        const ex = e as { status: CallbackStatus; data?: CompletionApplyData };
        const lendersStatus = ex.data?.lendersStatus;
        const approvals = lendersStatus?.filter(
          (lender) =>
            lender.status === 'PreApproved' ||
            lender.status === 'Approved' ||
            lender.status === 'PartiallyApproved' ||
            lender.status === 'Pending',
        );

        if (displayFlipScreen) {
          start();
          await flipScreen({
            origin: FlipOrigin.ApplyFailed,
            toCustomer: false,
            ...(!!lendersStatus &&
              !approvals?.length && {
                additionalMessageType: 'Decline',
              }),
          });
        }

        openPINScreen(sdkDisplayed);

        const errorNotAllowed = getIsErrorNotAllowed(ex.status?.code);
        logger[errorNotAllowed ? 'error' : 'warn'](
          `Prequalify failed. error: ${JSON.stringify(ex)}`,
          {
            filename: 'flowManager.ts',
            function: 'apply',
          },
        );

        if (errorNotAllowed)
          feedback({
            type: FEEDBACK_TYPE.GENERAL_ERROR,
          });
        return;
      } finally {
        setSdkLoading(false);
        setSdkDisplayed(false);
      }
    } catch (e) {
      return;
    } finally {
      stopLoading();
    }
  };

  return apply;
};

export const useIndependentApply = () => {
  const config = useInitConfigProps();
  const currency = useGetMerchantCurrency();
  const [feedback] = useFeedbackSnackbar();
  const ca = useInitializaPaymentSdk();
  const storeId = getStoreFromStorage()?.id;

  return async () => {
    try {
      if (!storeId) {
        feedback({ type: FEEDBACK_TYPE.GENERAL_ERROR });
        return;
      }

      const sdk = await ca(storeId);
      await sdk.updateSdkConfig({
        storeId,
      });

      try {
        const applyOpts: MerchantApplyOpt = {
          ...config,
          currency,
        };

        logger.log('Starting consumer self apply', {
          filename: 'flowManager',
          function: 'useIndependentApply',
          applyProps: applyOpts,
        });

        const applyResponse = await sdk.payments('apply', applyOpts);
        if (applyResponse.status) throw applyResponse;

        logger.log('Consumer self apply completed', {
          filename: 'flowManager',
          function: 'useIndependentApply',
          resultData: applyResponse,
        });

        const applyData = applyResponse.data;
        if (!applyData) {
          throw new Error('No apply data returned');
        }
      } catch (e: unknown) {
        const ex = e as { status: CallbackStatus; data?: CompletionApplyData };

        const errorNotAllowed = getIsErrorNotAllowed(ex.status?.code);
        logger[errorNotAllowed ? 'error' : 'warn'](
          `Consumer self apply failed. error: ${JSON.stringify(ex)}`,
          {
            filename: 'flowManager.ts',
            function: 'useIndependentApply',
          },
        );

        if (errorNotAllowed)
          feedback({
            type: FEEDBACK_TYPE.GENERAL_ERROR,
          });
        return;
      }
    } catch (e) {
      return;
    }
  };
};

export const useCheckout = (stopLoading: () => void) => {
  const cart = useGetCart();
  const consumer = useGetConsumer();
  const consumerId = useGetConsumerId();
  const updateConsumer = useConsumerData();
  const { start } = useIdleTimerContext();
  const prequalifyConfirmationToken = useGetPrequalifyConfirmationToken();
  const config = useInitConfigProps();
  const setSdkDisplayed = useSetSdkDisplayed();
  const setSdkLoading = useSetSdkLoading();
  const setConfirmationToken = useSetPrequalifyConfirmationToken();
  const getSignedApplicationId = useGetSignedApplicationId();
  const { flipScreen: displayFlipScreen } =
    useGetMerchantConfigurationOptions();
  const [feedback] = useFeedbackSnackbar();
  const openPINScreen = useOpenPinScreen();
  const sdkDisplayed = useGetSdkDisplayed();
  const flipScreen = useFlipScreen();
  const getPromotionCode = useGetPromotionCode();
  const checkoutConfig = useGetCheckoutConfiguration();
  const selectedLender = useGetApprovingLender();
  const updateCurrentModal = useSetCurrentModal();
  const userPermissions = useGetUserPermissions();
  const getSettlementConfiguration = useSettlementConfigurationModal();
  const merchantSelectedSettlementOption = useGetSelectedSettleOption();
  const updateOfferDescription = useSetOfferDescription();
  const currency = useGetMerchantCurrency();
  const ca = useInitializaPaymentSdk();
  const storeId = useGetStoreId();
  return async (
    applicationId?: string,
    isExpress?: boolean,
    latestLenderId?: string,
  ) => {
    try {
      //TODO: remove this log when incident #34555 resolved
      logger.log('Starting useCheckout hook ', {
        filename: 'flowManager.tsx',
        function: 'useCheckout',
        userPermissions,
        checkoutConfig,
      });

      if (!userPermissions.CheckoutExecute) {
        updateCurrentModal('checkoutDisabled');
        return;
      }

      if (!checkoutConfig) {
        feedback({
          type: FEEDBACK_TYPE.GENERAL_ERROR,
        });
        throw new Error('missing configuration');
      }

      const checkoutAllowed =
        (!selectedLender && !latestLenderId) ||
        checkoutConfig.lendersConfigurations.find(
          (lender) => lender.id === (selectedLender || latestLenderId),
        );

      if (!checkoutAllowed) {
        updateCurrentModal('checkoutLenderDisabled');
        return;
      }

      try {
        let updatedConsumer = consumer;
        if (consumerId) {
          updatedConsumer = await updateConsumer(consumerId);
        }

        const promoCode = await getPromotionCode(
          `${consumer?.firstName} ${consumer?.lastName}`,
          !!applicationId,
        );

        stopLoading();
        const settlementOption =
          merchantSelectedSettlementOption === 'AutomaticAndManual'
            ? await getSettlementConfiguration()
            : merchantSelectedSettlementOption;

        if (displayFlipScreen) {
          await flipScreen({
            origin: FlipOrigin.CheckoutStart,
            toCustomer: true,
          });
        }

        setSdkDisplayed(true);
        setSdkLoading(true);
        clearSavedUserSession();
        const cartAmounts = {
          totalAmount: cart.totalAmount,
          taxAmount: cart.taxAmount || 0,
          shippingAmount: cart.shippingAmount || 0,
        };

        const checkoutOpts: MerchantCheckoutOpt = {
          callback: () => {},
          ...config,
          consumerDetails: updatedConsumer,
          cartDetails: { ...cart },
          applicationId: await getSignedApplicationId(
            applicationId ?? prequalifyConfirmationToken,
          ),
          ...(!!promoCode && {
            promoCode,
          }),
          onModalOpen: () => setSdkLoading(false),
          onDataUpdate: () =>
            Promise.resolve({
              ...(cartAmounts as OnDataUpdateCallBackData),
            }),
          currency,
        };

        logger.log('Starting checkout', {
          filename: 'flowManager.ts',
          function: 'useCheckout',
          checkoutProps: checkoutOpts,
        });

        const sdk = await ca(storeId);
        await sdk.updateSdkConfig({
          storeId,
        });

        const checkoutResponse = await sdk.payments('checkout', checkoutOpts);

        if (!checkoutResponse.data || !checkoutResponse.token)
          throw checkoutResponse;

        const checkoutData = {
          ...checkoutResponse.data,
          token: checkoutResponse.token,
        } as CompletionCheckoutData & {
          token: string;
        };

        logger.log('Checkout completed', {
          filename: 'flowManager.ts',
          function: 'useCheckout',
          resultData: checkoutResponse,
        });

        if (isExpress) {
          setConfirmationToken(checkoutData.token);
        }
        addToContext('confirmationToken', checkoutData.token);
        updateOfferDescription(checkoutData.offer?.description ?? '');
        return { ...checkoutData, settlementOption };
      } catch (e: unknown) {
        if (displayFlipScreen) {
          start();
          await flipScreen({
            origin: FlipOrigin.CheckoutFailed,
            toCustomer: false,
          });
        }
        openPINScreen(sdkDisplayed);
        const ex = e as { status: CallbackStatus };
        const notAllowedErrors =
          ex.status?.code !== 'CONSUMER_CANCELLED' &&
          ex.status?.code !== 'BACK_TO_STORE' &&
          ex.status?.code !== 'PENDING';
        logger[notAllowedErrors ? 'error' : 'warn'](
          `Checkout exception occurred: ${JSON.stringify(ex)}`,
          {
            filename: 'flowManager.ts',
            function: 'useCheckout',
          },
        );
        setConfirmationToken(undefined);
        removeFromContext('consumer');
        removeFromContext('applicationId');
        removeFromContext('signedApplicationId');
        if (notAllowedErrors) {
          feedback({
            type: FEEDBACK_TYPE.PROCESS_PAYMENT_ERROR,
          });
        }
        return;
      } finally {
        /** TODO: remove when modal navigation fixed -
         * We observed that on Safari if navigation to /confirm happens right after confirmation callback we actually
         * staying on the numpad screen or taken back from /confirm to numpad screen. Waiting for 10ms before navigation
         * to /confirm solves this issue, we increased it to 100 to be on the safe side.
         */
        await awaiter(100);
        setSdkDisplayed(false);
        setSdkLoading(false);
      }
    } catch (e) {
      return;
    } finally {
      stopLoading();
    }
  };
};

export const useCreateCharge = () => {
  const { getAccessTokenSilently } = useAuth0();
  const [feedback] = useFeedbackSnackbar();
  const isAdvancedCart = useGetCartTypeToggle();
  const setSettlementData = useSetSettlementData();
  const advancedCartDetails = useGetAdvancedCartDetails();
  const singleCartMerchantOrderId = useGetMerchantOrderId();
  const merchantOrderId = isAdvancedCart
    ? advancedCartDetails.merchantOrderId
    : singleCartMerchantOrderId;
  const setErrorScreen = useErrorScreen();
  const openPINScreen = useOpenPinScreen();
  const sdkDisplayed = useGetSdkDisplayed();
  return async (
    selectedSettleOption: Omit<SettleOptions, 'AutomaticAndManual'>,
    confirmationToken: string,
    lenderId: string,
    pendingFlow?: boolean,
  ) => {
    try {
      const shouldSettle = selectedSettleOption === 'Automatic';
      const authToken = await getAccessTokenSilently();
      if (!authToken) {
        setErrorScreen();
        throw new Error('Auth0 token error useCreateCharge');
      }
      const chargesPath = `${environment.backendBaseUrl}/v2/payment/charges${
        shouldSettle ? '?settle=true' : ''
      }`;

      logger.log(`Send POST request to ${chargesPath}`, {
        filename: 'flowManager.ts',
        function: 'useCreateCharge',
        selectedSettleOption,
        shouldSettle,
        confirmationToken,
        merchantOrderId,
      });

      const response = await fetch(chargesPath, {
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({ confirmationToken, merchantOrderId }),
      });
      const data = await response.json();

      logger.log(`Received POST response from ${chargesPath}`, {
        filename: 'flowManager.ts',
        function: 'useCreateCharge',
        resultData: data,
      });

      if (!response.ok) {
        setErrorScreen();
        throw new Error(`General error ${await response.text()}`);
      }

      const postSaleData: {
        orderReferenceId: string;
        merchantOrderId: string;
        approvalCode: string;
        transactionsData: {
          lenderSettleReferenceId?: string | null;
          lenderAuthorizeReferenceId?: string | null;
        };
      } = data;

      const displayedReferenceIdByLender = getDisplayedReferenceId(
        postSaleData,
        lenderId,
      );

      setSettlementData({
        shouldSettle: shouldSettle,
        receivedData: true,
        orderId: postSaleData.merchantOrderId ?? postSaleData.orderReferenceId,
        lenderReferenceId: displayedReferenceIdByLender ?? '',
        approvalCode: postSaleData.approvalCode ?? '',
      });
      if (pendingFlow) {
        openPINScreen(sdkDisplayed);
      }
      feedback({ type: FEEDBACK_TYPE.CHARGE_SUCCESS });

      addToContext('merchantOrderId', postSaleData.merchantOrderId);

      if (shouldSettle && response.status === 202) {
        throw new Error('failed at settlement');
      }
    } catch (e) {
      logger.error(`Charge exception occurred: ${JSON.stringify(e)}`, {
        filename: 'flowManager.ts',
        function: 'useCreateCharge',
      });
      setErrorScreen();
    }
  };
};

export const useErrorScreen = () => {
  const setSettlementData = useSetSettlementData();
  return () => {
    setSettlementData({
      shouldSettle: false,
      receivedData: true,
      orderId: '',
      lenderReferenceId: '',
      approvalCode: '',
    });
  };
};

export const useLogout = () => {
  const { logout } = useAuth0();
  return () => {
    clearSavedUserSession();
    clearContext();
    logout({
      returnTo: `${window.location.origin}`,
    });
  };
};
